// Big Button Dashboard - 4 huge buttons, that's it!
import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, useTheme, useMediaQuery } from "@mui/material";
import {
  AttachMoney,
  Build,
  People,
  TrendingUp
} from "@mui/icons-material";

// BigButton component defined outside
const BigButton = ({ button, isMobile }) => (
  <Paper
    elevation={3}
    onClick={button.action}
    sx={{
      height: isMobile ? 150 : 200,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      background: button.gradient,
      color: "white",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "scale(1.05)",
        boxShadow: `0 8px 24px ${button.color}40`,
      },
      "&:active": {
        transform: "scale(0.98)",
      },
      borderRadius: 3,
      position: "relative",
      overflow: "hidden"
    }}
  >
    {button.icon}
    <Typography 
      variant={isMobile ? "h6" : "h5"} 
      sx={{ 
        mt: 1,
        fontWeight: "bold",
        textAlign: "center"
      }}
    >
      {button.title}
    </Typography>
    <Typography 
      variant="caption" 
      sx={{ 
        mt: 0.5,
        opacity: 0.9,
        fontSize: isMobile ? "0.75rem" : "0.875rem"
      }}
    >
      {button.description}
    </Typography>
  </Paper>
);

BigButton.propTypes = {
  button: PropTypes.shape({
    title: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    color: PropTypes.string.isRequired,
    gradient: PropTypes.string.isRequired,
    action: PropTypes.func.isRequired,
    description: PropTypes.string.isRequired
  }).isRequired,
  isMobile: PropTypes.bool.isRequired
};

function BigButtonDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const buttons = [
    {
      title: "Collect Rent",
      icon: <AttachMoney sx={{ fontSize: isMobile ? 48 : 64 }} />,
      color: "#4CAF50",
      gradient: "linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)",
      action: () => navigate("/financials/rent"),
      description: "Get paid"
    },
    {
      title: "Fix Stuff", 
      icon: <Build sx={{ fontSize: isMobile ? 48 : 64 }} />,
      color: "#2196F3",
      gradient: "linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)",
      action: () => navigate("/maintenance/requests"),
      description: "Handle repairs"
    },
    {
      title: "Find Tenants",
      icon: <People sx={{ fontSize: isMobile ? 48 : 64 }} />,
      color: "#9C27B0",
      gradient: "linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)",
      action: () => navigate("/tenants/applications"),
      description: "Fill vacancies"
    },
    {
      title: "See Money",
      icon: <TrendingUp sx={{ fontSize: isMobile ? 48 : 64 }} />,
      color: "#FF9800",
      gradient: "linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)",
      action: () => navigate("/dashboard"),
      description: "Track profits"
    }
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
        gap: isMobile ? 2 : 3,
        p: isMobile ? 2 : 3,
        maxWidth: 800,
        mx: "auto"
      }}
    >
      {buttons.map((button, index) => (
        <BigButton key={index} button={button} isMobile={isMobile} />
      ))}
    </Box>
  );
}

export default BigButtonDashboard;